import { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClipboardCheck, Compass } from 'lucide-react';
import { toast } from 'sonner';
import { sortPropertiesByCityAndProximity } from '../../lib/proximity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Crew Leader shift delegation: multi-select Site Agents, pick a property in
// scope, then delegate specific buildings + units for today's shift.
export const CrewDelegation = ({ user }) => {
  const [teamView, setTeamView] = useState(null);
  const [agentIds, setAgentIds] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [structure, setStructure] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState({}); // {building_number: Set-like array of units}
  const [verification, setVerification] = useState([]);
  const [closing, setClosing] = useState('');
  const [saving, setSaving] = useState(false);
  // Feb 2026 — Logistics Router. Crew Leader can filter the property picker
  // by "scoop" cities (this run's cherry-picked target list) and sort by
  // proximity so the daily route is the shortest possible drive.
  const [scoopCities, setScoopCities] = useState([]);
  const [proximitySortOn, setProximitySortOn] = useState(false);

  const loadDelegations = useCallback(async () => {
    try {
      const verRes = await fetch(`${BACKEND_URL}/api/company/crew-verification`, { credentials: 'include' });
      if (verRes.ok) {
        const v = await verRes.json();
        setVerification(v.delegations || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  const closeJob = async (scheduleId, force = false) => {
    setClosing(scheduleId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/crew-verification/${scheduleId}/close`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force }),
      });
      const data = await res.json();
      if (res.status === 409) {
        if (window.confirm(`${data.detail}\n\nClose anyway?`)) return closeJob(scheduleId, true);
        return;
      }
      if (!res.ok) throw new Error(data?.detail || 'Failed to close job');
      toast.success(data.message);
      await loadDelegations();
    } catch (e) {
      toast.error(e.message);
    } finally { setClosing(''); }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/routes/team-view`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setTeamView(data.find((e) => e.region?.region_id !== '_unassigned') || null);
        }
      } catch (e) { console.error(e); }
      loadDelegations();
    })();
  }, [loadDelegations]);

  const dedupe = (arr, key) => arr.filter((x, i, a) => a.findIndex((y) => y[key] === x[key]) === i);
  const agents = dedupe(teamView?.staff || [], 'user_id').filter((s) => s.role === 'agent');
  const properties = dedupe(teamView?.properties || [], 'property_id');
  const cities = [...new Set(properties.map((p) => (p.city || '').trim()).filter(Boolean))].sort();
  let visibleProperties = properties;
  if (scoopCities.length) {
    visibleProperties = visibleProperties.filter((p) => scoopCities.includes((p.city || '').trim()));
  }
  if (proximitySortOn) {
    visibleProperties = sortPropertiesByCityAndProximity(visibleProperties, scoopCities);
  }
  const toggleScoop = (c) =>
    setScoopCities((prev) => (prev.includes(c) ? prev.filter((v) => v !== c) : [...prev, c]));

  const toggleAgent = (uid) =>
    setAgentIds((prev) => (prev.includes(uid) ? prev.filter((v) => v !== uid) : [...prev, uid]));

  const pickProperty = async (pid) => {
    setPropertyId(pid);
    setStructure(null);
    setSelectedUnits({});
    try {
      const res = await fetch(`${BACKEND_URL}/api/properties/${pid}/structure`, { credentials: 'include' });
      if (res.ok) setStructure(await res.json());
    } catch (e) { console.error(e); }
  };

  const toggleBuilding = (b) => {
    setSelectedUnits((prev) => {
      const next = { ...prev };
      if (next[b.building_number]) delete next[b.building_number];
      else next[b.building_number] = [...(b.units || [])];
      return next;
    });
  };

  const toggleUnit = (bNum, unit) => {
    setSelectedUnits((prev) => {
      const units = prev[bNum] || [];
      const next = units.includes(unit) ? units.filter((u) => u !== unit) : [...units, unit];
      const copy = { ...prev };
      if (next.length === 0) delete copy[bNum];
      else copy[bNum] = next;
      return copy;
    });
  };

  const totalUnits = Object.values(selectedUnits).reduce((n, u) => n + u.length, 0);

  const save = async () => {
    if (agentIds.length === 0) { toast.error('Pick at least one Site Agent'); return; }
    if (!propertyId) { toast.error('Pick a community'); return; }
    setSaving(true);
    try {
      const building_units = Object.entries(selectedUnits).map(([building_number, units]) => ({ building_number, units }));
      const res = await fetch(`${BACKEND_URL}/api/company/shift-delegations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_ids: agentIds, property_id: propertyId, building_units }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to delegate shift');
      toast.success(data.message);
      setAgentIds([]);
      await loadDelegations();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-zinc-900 border-2 p-6 mb-2" style={{ borderColor: 'rgba(59, 130, 246, 0.5)' }} data-testid="crew-delegation-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-bold text-lg flex items-center text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          <ClipboardCheck className="h-5 w-5 mr-2 text-blue-400" />
          SHIFT DELEGATION · {teamView?.region ? `${teamView.region.region_code} ${teamView.region.name}` : '—'}
        </h3>
        <span className="text-xs text-zinc-400">
          Delegate specific buildings &amp; units to your Site Agents for today&rsquo;s shift.
        </span>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-300">Site Agents ({agentIds.length} selected)</Label>
            <div className="space-y-1.5 mt-1.5 max-h-36 overflow-y-auto pr-1" data-testid="delegation-agent-list">
              {agents.length === 0 ? (
                <p className="text-xs text-zinc-500">No Site Agents in your scope.</p>
              ) : agents.map((a) => (
                <button
                  key={a.user_id}
                  type="button"
                  onClick={() => toggleAgent(a.user_id)}
                  className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs border transition-colors ${
                    agentIds.includes(a.user_id)
                      ? 'bg-blue-600/25 border-blue-500 text-white'
                      : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-blue-500'
                  }`}
                  data-testid={`delegation-agent-${a.user_id}`}
                >
                  <span className="font-bold">{a.name}</span>
                  {a.badge_number && <span className="text-zinc-500 ml-1.5">{a.badge_number}</span>}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-zinc-300">Community</Label>
              <button
                type="button"
                onClick={() => setProximitySortOn((v) => !v)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${
                  proximitySortOn ? 'bg-amber-500/20 border-amber-400 text-amber-300' : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-amber-500'
                }`}
                data-testid="delegation-proximity-toggle"
              >
                <Compass className="h-3 w-3" /> {proximitySortOn ? 'Nearest first' : 'Sort by proximity'}
              </button>
            </div>
            {cities.length > 1 && (
              <div className="mb-1.5">
                <p className="text-[10px] font-bold text-purple-400 uppercase mb-1">
                  Scoop cities ({scoopCities.length || 'all'})
                </p>
                <div className="flex flex-wrap gap-1" data-testid="delegation-scoop-chips">
                  {cities.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleScoop(c)}
                      className={`px-1.5 py-0.5 rounded-full text-[10px] border transition-colors ${
                        scoopCities.includes(c)
                          ? 'bg-purple-600/25 border-purple-400 text-purple-200'
                          : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:border-purple-400'
                      }`}
                      data-testid={`delegation-scoop-${c.replace(/\s+/g, '-')}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Select value={propertyId} onValueChange={pickProperty}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="delegation-property-select">
                <SelectValue placeholder="Select a community" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {visibleProperties.map((p, idx) => (
                  <SelectItem key={p.property_id} value={p.property_id}>
                    {proximitySortOn ? `${idx + 1}. ` : ''}{p.property_name} · {p.total_units ?? '—'} units
                    {p.city ? <span className="text-zinc-500 ml-1">({p.city})</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {structure && (
            <div>
              <Label className="text-zinc-300">Buildings &amp; Units ({totalUnits} units selected)</Label>
              <div className="space-y-2 mt-1.5 max-h-56 overflow-y-auto pr-1" data-testid="delegation-building-list">
                {(structure.buildings || []).map((b) => {
                  const active = !!selectedUnits[b.building_number];
                  return (
                    <div key={b.building_number} className="bg-zinc-800 border border-zinc-700 rounded-md p-2">
                      <button
                        type="button"
                        onClick={() => toggleBuilding(b)}
                        className={`w-full text-left text-xs font-bold flex items-center justify-between ${active ? 'text-blue-400' : 'text-zinc-200'}`}
                        data-testid={`delegation-building-${b.building_number}`}
                      >
                        <span>Building {b.building_number}</span>
                        <Badge className={`text-[10px] ${active ? 'bg-blue-600 text-white' : 'bg-zinc-700 text-zinc-300'}`}>
                          {active ? `${(selectedUnits[b.building_number] || []).length}/${b.units.length}` : `${b.units.length} units`}
                        </Badge>
                      </button>
                      {active && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {b.units.map((u) => (
                            <button
                              key={u}
                              type="button"
                              onClick={() => toggleUnit(b.building_number, u)}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-mono border ${
                                (selectedUnits[b.building_number] || []).includes(u)
                                  ? 'bg-blue-600 border-blue-500 text-white'
                                  : 'bg-zinc-900 border-zinc-700 text-zinc-400'
                              }`}
                              data-testid={`delegation-unit-${b.building_number}-${u}`}
                            >
                              {u}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Button
            onClick={save}
            disabled={saving || agentIds.length === 0 || !propertyId}
            className="w-full bg-blue-600 text-white hover:opacity-90 font-bold"
            data-testid="delegation-save-btn"
          >
            {saving ? 'Delegating…' : `Delegate Shift (${agentIds.length} agents · ${totalUnits} units)`}
          </Button>
        </div>

        <div>
          <Label className="text-zinc-300">Today&rsquo;s delegations &amp; scan verification</Label>
          <div className="space-y-2 mt-1.5 max-h-96 overflow-y-auto pr-1" data-testid="delegation-today-list">
            {verification.length === 0 ? (
              <p className="text-xs text-zinc-500">No delegations logged for today yet.</p>
            ) : verification.map((v) => {
              const closed = v.status === 'verified_closed';
              return (
                <Card key={v.schedule_id} className="bg-zinc-800 border-zinc-700 p-2.5" data-testid={`verification-row-${v.schedule_id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-white">{v.agent_name}</p>
                    <div className="flex items-center gap-1.5">
                      {v.carried_forward && <Badge className="bg-zinc-700 text-zinc-300 text-[9px]">routine</Badge>}
                      <Badge className={`text-[10px] ${closed ? 'bg-emerald-700 text-white' : v.all_covered ? 'bg-blue-600 text-white' : 'bg-amber-700 text-white'}`}
                             data-testid={`verification-status-${v.schedule_id}`}>
                        {closed ? 'VERIFIED · CLOSED' : v.all_covered ? 'ALL UNITS COVERED' : `${v.covered_units}/${v.assigned_units} units`}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-0.5">{v.property_name}</p>
                  {(v.buildings || []).map((b) => (
                    <div key={b.building_number} className="flex flex-wrap gap-1 mt-1">
                      <span className="text-[10px] font-bold text-zinc-500 mr-0.5">B{b.building_number}:</span>
                      {(b.units || []).map((u) => (
                        <span
                          key={u.unit}
                          title={u.status || 'not scanned yet'}
                          className={`px-1 py-0.5 rounded text-[9px] font-mono border ${
                            u.status === 'completed' ? 'bg-emerald-900/60 border-emerald-600 text-emerald-300'
                            : u.status === 'recycle' ? 'bg-blue-900/60 border-blue-600 text-blue-300'
                            : u.status === 'missed' ? 'bg-amber-900/60 border-amber-600 text-amber-300'
                            : u.status === 'violation' ? 'bg-red-900/60 border-red-600 text-red-300'
                            : u.status === 'no_placement' ? 'bg-zinc-700 border-zinc-500 text-zinc-300'
                            : 'bg-zinc-900 border-zinc-700 text-zinc-500'
                          }`}
                        >
                          {u.unit}
                        </span>
                      ))}
                    </div>
                  ))}
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-zinc-500">
                      {closed ? `closed by ${v.verified_by_name}` : v.all_covered
                        ? 'System check: every unit accounted for (trash/recycle picked, violation filed, or no placement).'
                        : 'Units still unscanned — check before closing.'}
                    </p>
                    {!closed && (
                      <Button
                        size="sm"
                        onClick={() => closeJob(v.schedule_id)}
                        disabled={closing === v.schedule_id}
                        className="bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-[10px] h-6 px-2"
                        data-testid={`verification-close-btn-${v.schedule_id}`}
                      >
                        {closing === v.schedule_id ? 'Closing…' : 'Verify & Close'}
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};
